import {
  a,
  button,
  div,
  fnstate,
  goTo,
  h1,
  h2,
  span,
  table,
  thead,
  tbody,
  tr,
  th,
  td
} from '@srfnstack/fntags'
import { extensionId } from '../../state.js'
import { validateCredits } from '../../utils/credits.js'
import { pingExtension, scrapeEtsy } from '../../utils/extension.js'
import { EtsyIngest } from './EtsyIngest.js'
import { ManualPrompt } from './ManualPrompt.js'
import { fetchDefaultImageModel } from '../../utils/settings.js'

export const GenerateArtwork = () => {
  const ingestStatus = fnstate({ loading: false, message: '', type: '' })
  const manualPrompt = fnstate('')
  const manualBackground = fnstate('AUTO')
  const manualImage = fnstate(null)
  const manualImageInstructions = fnstate('')
  const singleUrl = fnstate('')
  const extensionInstalled = fnstate(false)
  const batchSize = fnstate(5)
  const csvBatchSize = fnstate(5)
  const manualBatchSize = fnstate(5)
  const csvUrlCount = fnstate(0)
  const activeTab = fnstate('manual')
  const imageModel = fnstate('bfl_flux2pro')
  fetchDefaultImageModel().then(m => imageModel(m))

  // Design Seeds State
  const seedItems = fnstate([])
  const seedTotal = fnstate(0)
  const seedLoading = fnstate(false)
  const seedLoadingMore = fnstate(false)
  const SEED_LIMIT = 20

  const fetchSeeds = async () => {
    seedLoading(true)
    try {
      const res = await fetch(`/api/design-seeds?offset=0&limit=${SEED_LIMIT}&_=${Date.now()}`)
      if (res.ok) {
        const data = await res.json()
        seedItems(data.signals)
        seedTotal(data.total)
      }
    } catch (err) {
      console.error('Failed to fetch seeds:', err)
    } finally {
      seedLoading(false)
    }
  }

  const loadMoreSeeds = async () => {
    if (seedLoadingMore() || seedItems().length >= seedTotal()) return
    seedLoadingMore(true)
    try {
      const res = await fetch(`/api/design-seeds?offset=${seedItems().length}&limit=${SEED_LIMIT}&_=${Date.now()}`)
      if (res.ok) {
        const data = await res.json()
        seedItems([...seedItems(), ...data.signals])
        seedTotal(data.total)
      }
    } catch (err) {
      console.error('Failed to load more seeds:', err)
    } finally {
      seedLoadingMore(false)
    }
  }

  const seedSentinel = div({ class: 'h-4' })
  const seedObserver = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) loadMoreSeeds()
  }, { rootMargin: '200px' })
  seedObserver.observe(seedSentinel)

  const scrapeWithSeed = async (signal, bSize) => {
    if (!await validateCredits('ETSY_ANALYSIS', { batch_size: bSize })) return
    try {
      const response = await scrapeEtsy(extensionId(), {
        designSeedId: signal.id,
        url: signal.product_link,
        callbackUrl: `${window.location.origin}/api/worker/complete-scrape`,
        batchSize: parseInt(bSize),
        imageModel: imageModel()
      })
      if (response && response.success) {
        if (response.concept_prompt_id) {
          goTo(`/artwork-review/${response.concept_prompt_id}`)
        } else {
          await window.alert('Scrape completed and saved!')
          fetchSeeds()
        }
      } else {
        await window.alert('Scrape started but failed: ' + (response?.error || 'Unknown error'))
      }
    } catch (err) {
      await window.alert(err.message)
    }
  }

  const reAnalyze = async (signalId, bSize) => {
    if (!await validateCredits('ETSY_ANALYSIS', { batch_size: bSize })) return

    const instructions = await window.prompt('Additional instructions for AI analysis (optional):', '', 'Re-Analyze Seed')
    if (instructions === null) return

    try {
      const res = await fetch(`/api/design-seeds/${signalId}/re-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batch_size: parseInt(bSize),
          additional_instructions: instructions
        })
      })
      if (res.ok) {
        await window.alert('Re-analysis triggered successfully.')
        fetchSeeds()
      } else {
        const data = await res.json()
        await window.alert('Failed to re-analyze: ' + (data.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Error re-analyzing:', err)
      await window.alert('Error re-analyzing.')
    }
  }

  const scrapeMultipliers = fnstate({ batch_size: 20 })
  batchSize.subscribe(b => scrapeMultipliers({ batch_size: b }))

  const manualMultipliers = fnstate({ batch_size: 20 })
  manualBatchSize.subscribe(b => manualMultipliers({ batch_size: b }))

  const checkExtension = () => {
    if (extensionId()) {
      pingExtension(extensionId()).then(ok => extensionInstalled(ok))
    }
  }

  checkExtension()
  setInterval(checkExtension, 5000)
  fetchSeeds(0)

  const scrapeWithExtension = async (id, url) => {
    try {
      const response = await scrapeEtsy(extensionId(), {
        designSeedId: id,
        url,
        callbackUrl: `${window.location.origin}/api/worker/complete-scrape`,
        batchSize: batchSize(),
        imageModel: imageModel()
      })
      if (response && response.success) {
        ingestStatus({ loading: false, message: 'Scrape completed successfully!', type: 'success' })
        singleUrl('')
        if (response.concept_prompt_id) {
          goTo(`/artwork-review/${response.concept_prompt_id}`)
        } else {
          fetchSeeds(0)
        }
      } else {
        ingestStatus({ loading: false, message: 'Scrape started but failed: ' + (response?.error || 'Unknown error'), type: 'error' })
      }
    } catch (err) {
      ingestStatus({ loading: false, message: err.message, type: 'error' })
    }
  }

  const handleUrlIngest = async () => {
    if (!singleUrl()) return
    ingestStatus({ loading: true, message: 'Ingesting URL...', type: 'info' })

    try {
      const res = await fetch('/api/ingest/url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: singleUrl(),
          batch_size: batchSize()
        })
      })

      const result = await res.json()

      if (res.ok) {
        if (result.exists) {
          ingestStatus({
            loading: false,
            message: 'This URL was already in your seeds. Triggering scrape...',
            type: 'info'
          })
        } else {
          ingestStatus({
            loading: true,
            message: 'URL ingested. Triggering scrape...',
            type: 'info'
          })
        }
        await scrapeWithExtension(result.id, singleUrl())
      } else {
        ingestStatus({
          loading: false,
          message: result.error || 'Ingest failed.',
          type: 'error'
        })
      }
    } catch (err) {
      ingestStatus({ loading: false, message: 'Network error.', type: 'error' })
    }
  }

  const handleUpload = async (file) => {
    if (!file) return
    ingestStatus({ loading: true, message: 'Ingesting CSV...', type: 'info' })

    const formData = new FormData()
    formData.append('file', file)
    formData.append('batch_size', csvBatchSize())

    try {
      const res = await fetch('/api/ingest/upload', {
        method: 'POST',
        body: formData
      })

      const result = await res.json()

      if (res.ok) {
        ingestStatus({
          loading: false,
          message: `Successfully ingested ${result.count} records. Processing...`,
          type: 'success'
        })
        fetchSeeds(0)
      } else {
        ingestStatus({
          loading: false,
          message: result.error || 'Upload failed.',
          type: 'error'
        })
      }
    } catch (err) {
      ingestStatus({ loading: false, message: 'Network error.', type: 'error' })
    }
  }

  const handleManualGenerate = async () => {
    if (!manualPrompt()) return
    ingestStatus({ loading: true, message: 'Processing manual prompt...', type: 'info' })

    const formData = new FormData()
    formData.append('prompt', manualPrompt())
    formData.append('background_type', manualBackground())
    formData.append('batch_size', manualBatchSize())
    formData.append('image_model', imageModel())
    if (manualImage()) {
      formData.append('image', manualImage())
      if (manualImageInstructions()) {
        formData.append('image_instructions', manualImageInstructions())
      }
    }

    try {
      const res = await fetch('/api/ingest/prompt', {
        method: 'POST',
        body: formData
      })
      const result = await res.json()
      if (res.ok) {
        manualPrompt('')
        manualImage(null)
        manualImageInstructions('')
        ingestStatus({ loading: false, message: 'Manual prompt enqueued successfully!', type: 'success' })
        if (result.id) {
          goTo(`/artwork-review/${result.id}`)
        } else {
          goTo('/artwork-review')
        }
      } else {
        const err = result
        ingestStatus({ loading: false, message: err.error || 'Failed to enqueue.', type: 'error' })
      }
    } catch (err) {
      ingestStatus({ loading: false, message: 'Network error.', type: 'error' })
    }
  }

  const handleLuckyGenerate = async () => {
    ingestStatus({ loading: true, message: 'Conjuring a random concept...', type: 'info' })

    const formData = new FormData()
    formData.append('lucky', 'true')
    formData.append('batch_size', manualBatchSize())
    formData.append('background_type', manualBackground())
    formData.append('image_model', imageModel())

    try {
      const res = await fetch('/api/ingest/prompt', {
        method: 'POST',
        body: formData
      })
      const result = await res.json()
      if (res.ok) {
        ingestStatus({ loading: false, message: '', type: '' })
        if (result.id) {
          goTo(`/artwork-review/${result.id}`)
        } else {
          goTo('/artwork-review')
        }
      } else {
        ingestStatus({ loading: false, message: result.error || 'Failed to generate.', type: 'error' })
      }
    } catch (err) {
      ingestStatus({ loading: false, message: 'Network error.', type: 'error' })
    }
  }

  const handleFileSelect = async (file) => {
    if (!file) return
    const text = await file.text()
    const lines = text.split(/\r?\n/)
    let count = 0
    for (const line of lines) {
      if (line.toLowerCase().includes('etsy.com/listing/')) {
        count++
      }
    }
    csvUrlCount(count)
  }

  const tabBtn = (id, label) => button({
    class: activeTab.bindAttr(t => `tab font-bold ${t === id ? 'tab-active' : ''}`),
    onclick: () => activeTab(id)
  }, label)

  return div({ class: 'p-6 space-y-6' },
    div({ class: 'flex items-center justify-between' },
      h1({ class: 'text-3xl font-bold' }, 'Generate Artwork')
    ),

    div({ class: 'tabs tabs-border tabs-lg' },
      tabBtn('manual', 'Manual Creation'),
      tabBtn('research', 'Research & Scrape')
    ),

    activeTab.bindAs(tab => {
      if (tab === 'research') {
        return div({ class: 'space-y-8' },
          EtsyIngest({
            extensionInstalled,
            batchSize,
            ingestStatus,
            singleUrl,
            scrapeMultipliers,
            csvBatchSize,
            csvUrlCount,
            handleUrlIngest,
            handleFileSelect,
            handleUpload,
            imageModel
          }),

          div({ class: 'space-y-4' },
            div({ class: 'flex justify-between items-end border-b border-base-300 pb-2' },
              div({ class: 'flex items-center gap-3' },
                h2({ class: 'text-xl font-bold' }, 'Design Seeds'),
                seedTotal.bindAs(t => t > 0 ? span({ class: 'badge badge-ghost font-mono' }, t) : span())
              ),
              button({
                class: 'btn btn-ghost btn-xs gap-1',
                onclick: () => fetchSeeds()
              }, span({ class: 'material-symbols-rounded !text-sm' }, 'refresh'), 'Refresh')
            ),

            seedLoading.bindAs(l => l
              ? div({ class: 'flex justify-center p-20' }, span({ class: 'loading loading-spinner loading-lg text-primary' }))
              : seedItems.bindAs(list => {
                if (!list || list.length === 0) {
                  return div({ class: 'bg-base-200/50 rounded-xl border border-dashed border-base-content/20 p-12 text-center opacity-60 flex flex-col items-center gap-2' },
                    span({ class: 'material-symbols-rounded text-5xl' }, 'eco'),
                    span('No seeds in pipeline. Ingest a URL or CSV above to start.')
                  )
                }

                return div({ class: 'flex flex-col gap-2' },
                  div({ class: 'overflow-x-auto bg-base-100 rounded-xl border border-base-300 shadow-sm' },
                    table({ class: 'table table-sm' },
                      thead(
                        tr(
                          th('ID'),
                          th('Name'),
                          th('Status'),
                          th('Source'),
                          th('Created'),
                          th('Link'),
                          th('')
                        )
                      ),
                      tbody(
                        list.map(s =>
                          tr({
                            class: 'hover:bg-base-200 cursor-pointer transition-colors',
                            onclick: () => goTo(`/design-seeds/${s.id}`)
                          },
                          td({ class: 'font-mono text-xs opacity-50' }, s.id),
                          td({ class: 'max-w-md' },
                            div({ class: 'font-medium text-sm truncate' }, s.product_name || 'Untitled')
                          ),
                          td(
                            span({ class: `badge badge-sm ${s.processed ? 'badge-success' : 'badge-warning'} font-bold border-none` },
                              s.processed ? 'Processed' : 'Unprocessed'
                            )
                          ),
                          td({ class: 'text-xs opacity-50' }, s.source || ''),
                          td({ class: 'text-xs opacity-40' }, new Date(s.created_at).toLocaleDateString()),
                          td({ onclick: (e) => e.stopPropagation() },
                            a({ href: s.product_link, target: '_blank', class: 'link link-primary text-xs font-bold' }, 'Etsy')
                          ),
                          td({ onclick: (e) => e.stopPropagation() },
                            s.processed
                              ? button({ class: 'btn btn-outline btn-secondary btn-xs', onclick: () => reAnalyze(s.id, 1) }, 'Re-Analyze')
                              : button({ class: 'btn btn-primary btn-xs', onclick: () => scrapeWithSeed(s, 1) }, 'Scrape')
                          )
                          )
                        )
                      )
                    )
                  ),
                  seedLoadingMore.bindAs(lm => lm
                    ? div({ class: 'flex justify-center py-4' }, span({ class: 'loading loading-spinner loading-md text-primary' }))
                    : div()
                  ),
                  seedSentinel
                )
              })
            )
          )
        )
      }

      return ManualPrompt({
        manualPrompt,
        manualBatchSize,
        manualBackground,
        manualImage,
        manualImageInstructions,
        manualMultipliers,
        ingestStatus,
        handleManualGenerate,
        handleLuckyGenerate,
        imageModel
      })
    })
  )
}
